import type { ImageCrop } from '@/queries/notifications';

export interface AndroidFrameProps {
  state: 'banner' | 'expanded';
  title: string;
  body: string;
  appName: string;
  imageUrl?: string;
  imageCrop: ImageCrop;
  imageBgColor?: string;
}

const PADEL_ICON =
  'https://cdn.shopify.com/s/files/1/0654/5095/4474/files/logo-icon.png';

export function AndroidNotificationFrame({
  state,
  title,
  body,
  appName,
  imageUrl,
  imageCrop,
  imageBgColor,
}: AndroidFrameProps) {
  const showImage = imageCrop !== 'hidden' && Boolean(imageUrl);
  const aspectStyle = aspectRatioFor(imageCrop);

  return (
    <div style={androidOuter}>
      {/* Status bar */}
      <div style={androidStatusBar}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>9:41</span>
        <span style={{ fontSize: 12, opacity: 0.8 }}>4G ▮▮▮▮ 84%</span>
      </div>

      {/* Lock screen time */}
      <div style={androidLockHero}>
        <div style={androidLockTime}>9:41</div>
        <div style={androidLockDate}>الأحد، 25 مايو</div>
      </div>

      {/* Notification card */}
      <div style={androidNotifCard}>
        <div style={androidNotifHeader}>
          <div style={androidAppIcon}>
            <img
              src={PADEL_ICON}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={androidAppName}>{appName}</div>
            <div style={androidTimeRow}>• now</div>
          </div>
        </div>

        <div style={androidNotifBody}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={androidTitle}>{title || 'عنوان الإشعار هيظهر هنا'}</div>
            <div
              style={{
                ...androidBody,
                WebkitLineClamp: state === 'banner' ? 1 : 4,
              }}
            >
              {body || 'نص الإشعار. هيظهر تحت العنوان.'}
            </div>
          </div>
          {state === 'banner' && showImage ? (
            <div style={androidBannerThumb}>
              <img
                src={imageUrl}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  background: imageBgColor || 'transparent',
                }}
              />
            </div>
          ) : null}
        </div>

        {state === 'expanded' && showImage ? (
          <div
            style={{
              ...androidExpandedImageWrap,
              ...aspectStyle,
              background: imageBgColor || '#1a1a1a',
            }}
          >
            <img
              src={imageUrl}
              alt=""
              style={{
                maxWidth: '92%',
                maxHeight: '92%',
                objectFit: 'contain',
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

const androidOuter: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 280,
  minHeight: 520,
  margin: '0 auto',
  borderRadius: 32,
  overflow: 'hidden',
  background:
    'linear-gradient(165deg, #1d2438 0%, #2c1f5a 40%, #1c1746 100%)',
  border: '4px solid #0a0a0a',
  boxShadow:
    '0 24px 60px -20px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)',
  fontFamily: 'Roboto, "Helvetica Neue", system-ui',
  color: '#fff',
};

const androidStatusBar: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '14px 18px 4px',
  fontSize: 12,
};

const androidLockHero: React.CSSProperties = {
  padding: '24px 18px 20px',
};

const androidLockTime: React.CSSProperties = {
  fontSize: 60,
  fontWeight: 300,
  letterSpacing: -2,
  lineHeight: 1,
};

const androidLockDate: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 400,
  opacity: 0.85,
  marginTop: 6,
};

const androidNotifCard: React.CSSProperties = {
  margin: '8px 10px 0',
  borderRadius: 28,
  background: 'rgba(28, 27, 31, 0.78)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  padding: '14px 16px',
  border: '1px solid rgba(255,255,255,0.06)',
  overflow: 'hidden',
};

const androidNotifHeader: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
};

const androidAppIcon: React.CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: 50,
  overflow: 'hidden',
  background: '#fff',
  flexShrink: 0,
};

const androidAppName: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  opacity: 0.9,
  letterSpacing: 0.1,
};

const androidTimeRow: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.65,
  marginTop: 1,
};

const androidNotifBody: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
};

const androidTitle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 500,
  lineHeight: 1.25,
  marginBottom: 2,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical' as const,
};

const androidBody: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.35,
  opacity: 0.88,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
};

const androidBannerThumb: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 8,
  overflow: 'hidden',
  flexShrink: 0,
};

const androidExpandedImageWrap: React.CSSProperties = {
  marginTop: 12,
  borderRadius: 12,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6%',
};

function aspectRatioFor(crop: ImageCrop): React.CSSProperties {
  switch (crop) {
    case 'square':
      return { aspectRatio: '1 / 1' };
    case 'landscape':
      return { aspectRatio: '16 / 9' };
    case 'portrait':
      return { aspectRatio: '3 / 4' };
    case 'hidden':
      return { display: 'none' };
    case 'auto':
    default:
      return { aspectRatio: '16 / 10' };
  }
}
